<template>
    <div
        class="date-picker-input group relative flex h-input items-center rounded-input border border-muval-gray-4 bg-white text-sm transition hover:border-muval-gray-3"
        :class="[
            !showPlaceholder && highlightOnValue && '!border-focus',
            errorMessage && 'border-muval-error',
            small && '!h-7',
            disabled && 'pointer-events-none !bg-muval-gray-5',
            !!timeSuffix && 'has-time-suffix',
            {
                'w-[325px]': isRange && hasTime && !timeSuffix,
                'w-[191px]': isRange && !hasTime && !timeSuffix,
                'w-[190px]': !isRange && hasTime && !timeSuffix,
                'w-[120px]': !isRange && !hasTime && !timeSuffix,
                // Wider when a time suffix is appended to the date so it stays on one line
                // (plus room reserved on the right for the clear button — see .has-time-suffix).
                'w-[380px]': isRange && !!timeSuffix,
                'w-[280px]': !isRange && !!timeSuffix,
            },
        ]"
    >
        <VueDatePicker
            ref="datePickerRef"
            v-model="internalValue"
            :range="isRange ? { partialRange: false, disableTimeRangeValidation: isDisableTimeRangeValidation } : false"
            :time-config="{
                enableTimePicker: hasTime || timeOnly,
                is24: false,
                timePickerInline: !timeOnly,
            }"
            :time-picker="timeOnly"
            :format="dateFormat"
            :formats="formatsConfig"
            :disabled="disabled"
            :min-date="minDate || undefined"
            :max-date="maxDate || undefined"
            :teleport="teleport"
            :floating="{ strategy: 'fixed' }"
            :highlight="{ dates: highlightDates }"
            :auto-apply="true"
            :close-on-auto-apply="false"
            :multi-calendars="props.pages?.toString() || ($props.isRange ? '2' : undefined)"
            :input-attrs="{ clearable: enableClearable }"
            class="dp-custom"
        >
            <template #action-extra>
                <slot
                    name="action-extra"
                    :is-range="isRange"
                />
            </template>
            <template #input-icon>
                <template v-if="hasIcon">
                    <template v-if="calendarIcon">
                        <SvgIcon
                            :name="calendarIcon"
                            class="custom-icon-placeholder pointer-events-none size-[18px] h-full text-muval-gray-4 transition-colors group-hover:text-muval-gray-3"
                        />
                    </template>
                    <template v-else>
                        <svg
                            v-if="!timeOnly"
                            class="pointer-events-none h-full w-3.5 text-muval-gray-4 transition-colors group-hover:text-muval-gray-3"
                            width="15"
                            height="16"
                            viewBox="0 0 15 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.4 8.8H8C7.56 8.8 7.2 9.16 7.2 9.6V12C7.2 12.44 7.56 12.8 8 12.8H10.4C10.84 12.8 11.2 12.44 11.2 12V9.6C11.2 9.16 10.84 8.8 10.4 8.8ZM10.4 0.8V1.6H4V0.8C4 0.36 3.64 0 3.2 0C2.76 0 2.4 0.36 2.4 0.8V1.6H1.6C0.712 1.6 0.00799999 2.32 0.00799999 3.2L0 14.4C0 15.28 0.712 16 1.6 16H12.8C13.68 16 14.4 15.28 14.4 14.4V3.2C14.4 2.32 13.68 1.6 12.8 1.6H12V0.8C12 0.36 11.64 0 11.2 0C10.76 0 10.4 0.36 10.4 0.8ZM12 14.4H2.4C1.96 14.4 1.6 14.04 1.6 13.6V5.6H12.8V13.6C12.8 14.04 12.44 14.4 12 14.4Z"
                                fill="currentColor"
                            />
                        </svg>

                        <svg
                            v-else
                            class="custom-icon-placeholder pointer-events-none h-full w-4 text-muval-gray-4 transition-colors group-hover:text-muval-gray-3"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="9"
                                stroke="currentColor"
                                stroke-width="2"
                            />
                            <path
                                d="M12 7V12L15 14"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </template>
                </template>
            </template>
            <template
                v-if="customTrigger"
                #trigger
            >
                <slot name="trigger" />
            </template>
            <template #action-row="opts">
                <slot
                    name="action-row"
                    v-bind="opts"
                >
                    <MuvalButton
                        tertiary
                        type="button"
                        class="w-full"
                        small
                        @click="opts.selectDate"
                        >Apply</MuvalButton
                    >
                </slot>
            </template>
        </VueDatePicker>
        <template v-if="placeholder">
            <div
                class="custom-placeholder pointer-events-none absolute text-muval-gray-3 transition-all group-hover:text-muval-gray-2 skeleton:hidden"
                :class="showPlaceholder ? 'visible top-1/2 ml-8 -translate-y-1/2' : 'hidden'"
            >
                <span>{{ placeholder }}</span>
            </div>
        </template>
        <template v-else>
            <div
                v-if="label"
                class="pointer-events-none absolute -translate-y-1/2 text-muval-gray-3 transition-all group-hover:text-muval-gray-2 skeleton:hidden"
                :class="showPlaceholder ? 'top-1/2 ml-8' : 'top-0 ml-2 text-xs'"
            >
                <span class="z-1 whitespace-nowrap">{{ label }}</span>
                <div class="absolute top-1/2 -z-1 -ml-0.5 h-[3px] w-[calc(100%+4px)] -translate-y-1/2 bg-white"></div>
            </div>
        </template>
        <div
            class="pointer-events-none absolute -bottom-5 left-0 w-full truncate whitespace-nowrap text-xxs"
            :class="errorMessage ? 'text-error' : ''"
            :title="errorMessage"
        >
            <span class="text-xxs">{{ errorMessage }}</span>
        </div>
        <div class="skeleton:loader"></div>
    </div>
</template>
<script setup lang="ts">
import { TimeModel, VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import dayjs from 'dayjs';
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen';

const props = defineProps({
    isRange: {
        type: Boolean,
        default: false,
        required: false,
    },
    hasTime: {
        type: Boolean,
        default: false,
        required: false,
    },
    timeOnly: {
        type: Boolean,
        default: false,
        required: false,
    },
    // Single value props (used when isRange is false)
    modelValue: {
        type: [String, Date, null],
        default: undefined,
        required: false,
    },
    vid: {
        type: String,
        default: () => idGen(6),
        required: false,
    },
    // Range value props (used when isRange is true)
    startDate: {
        type: [String, Date, null],
        default: undefined,
        required: false,
    },
    startVid: {
        type: String,
        default: () => idGen(6),
        required: false,
    },
    endDate: {
        type: [String, Date, null],
        default: undefined,
        required: false,
    },
    endVid: {
        type: String,
        default: () => idGen(6),
        required: false,
    },
    disabled: {
        type: Boolean,
        default: false,
        required: false,
    },
    minDate: {
        type: [Date, String, null],
        default: null,
        required: false,
    },
    maxDate: {
        type: [Date, String, null],
        default: null,
        required: false,
    },
    label: {
        type: [String, null],
        default: null,
        required: false,
    },
    placeholder: {
        type: [String, null],
        default: null,
        required: false,
    },
    highlightOnValue: {
        type: Boolean,
        default: false,
        required: false,
    },
    hasIcon: {
        type: Boolean,
        default: true,
        required: false,
    },
    small: {
        type: Boolean,
        default: false,
        required: false,
    },
    customTrigger: {
        type: Boolean,
        default: false,
        required: false,
    },
    pages: {
        type: [String, undefined],
        default: undefined,
        required: false,
    },
    highlightDates: {
        type: [Array],
        default: () => [],
        required: false,
    },
    calendarIcon: {
        type: [String, null],
        default: null,
        required: false,
    },
    enableClearable: {
        type: Boolean,
        default: true,
        required: false,
    },
    isDisableTimeRangeValidation: {
        type: Boolean,
        default: false,
        required: false,
    },
    // Teleport the picker menu to <body> so it escapes overflow-hidden
    // parents (e.g. modals). Off by default to preserve existing positioning.
    teleport: {
        type: Boolean,
        default: false,
        required: false,
    },
    // Optional time-of-day text appended to the date in the input (e.g. "9:00 AM – 11:00 AM"),
    // so a separately-stored time stays visible while the picker is closed.
    timeSuffix: {
        type: [String, null],
        default: null,
        required: false,
    },
});

const emit = defineEmits(['update:modelValue', 'update:startDate', 'update:endDate']);

const datePickerRef = ref();

// Use vee-validate's useField - different for single vs range
const { value: fieldValue, errorMessage: singleErrorMessage } = useField(props.vid, undefined, {
    initialValue: props.modelValue,
    valueProp: props.modelValue,
});

// Range fields
const { value: startDateValue, errorMessage: startErrorMessage } = useField(props.startVid, undefined, {
    initialValue: props.startDate,
    valueProp: props.startDate,
});

const { value: endDateValue, errorMessage: endErrorMessage } = useField(props.endVid, undefined, {
    initialValue: props.endDate,
    valueProp: props.endDate,
});

// Combined error message
const errorMessage = computed(() => {
    if (props.isRange) {
        return startErrorMessage.value || endErrorMessage.value;
    }
    return singleErrorMessage.value;
});

// Computed date format based on props
const dateFormat = computed(() => {
    if (props.timeOnly) {
        return 'HH:mm';
    }
    if (props.hasTime) {
        return props.isRange ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy HH:mm';
    }
    return props.isRange ? 'dd/MM/yyyy' : 'dd/MM/yyyy';
});

// Formats configuration to ensure time is not displayed when hasTime is false
const formatsConfig = computed(() => {
    if (props.timeOnly) {
        return {
            input: 'h:mm aa',
        };
    }
    // When a time suffix is supplied, take over input formatting: render the date(s) and
    // append the time so it shows even with the picker closed. (vue-datepicker accepts a
    // function for `formats.input` whose argument is the selected date value(s).)
    if (props.timeSuffix) {
        const formatDate = (value: unknown): string => (value ? dayjs(value as Date).format('DD/MM/YY') : '');
        return {
            input: (value: Date | Date[] | null) => {
                const datePart =
                    Array.isArray(value) ? value.map(formatDate).filter(Boolean).join(' - ') : formatDate(value);
                return datePart ? `${datePart} · ${props.timeSuffix}` : '';
            },
        };
    }
    if (props.hasTime) {
        return {
            input: props.isRange ? 'dd/MM/yy h:mm aa' : 'dd/MM/yy h:mm aa',
        };
    }
    // When hasTime is false, only show date without time
    return {
        input: props.isRange ? 'dd/MM/yy' : 'dd/MM/yy',
    };
});

// Internal value that syncs with both v-model and useField
const internalValue = computed({
    get: (): Date | Date[] | TimeModel | null => {
        // Handle range values
        if (props.isRange) {
            const startVal = startDateValue.value || props.startDate;
            const endVal = endDateValue.value || props.endDate;

            if (!startVal && !endVal) return null;

            if (props.timeOnly) {
                // Either side may be unset (a single custom time has a "from" but no "by"),
                // so guard each split — calling .split on a null/undefined value throws and
                // breaks the popup render, which makes the parent date picker fail to open.
                const toTimeModel = (value: string | null | undefined): TimeModel | null => {
                    if (!value || typeof value !== 'string') return null;
                    const [hours, minutes] = value.split(':');
                    return { hours: parseInt(hours), minutes: parseInt(minutes) };
                };

                const startModel = toTimeModel(startVal);
                const endModel = toTimeModel(endVal);

                if (startModel && endModel) {
                    return [startModel, endModel];
                }
                if (startModel) {
                    return [startModel, null] as any;
                }
                return null;
            }

            const start =
                startVal ?
                    typeof startVal === 'string' ? dayjs(startVal).toDate()
                    : startVal instanceof Date ? startVal
                    : new Date(startVal)
                :   null;

            const end =
                endVal ?
                    typeof endVal === 'string' ? dayjs(endVal).toDate()
                    : endVal instanceof Date ? endVal
                    : new Date(endVal)
                :   null;

            if (start && end) {
                return [start, end];
            }
            if (start) {
                return [start, null] as any;
            }
            return null;
        }

        // Handle single values
        const val = fieldValue.value || props.modelValue;
        if (!val) return null;

        if (typeof val === 'string') {
            // For time-only, create a date with today's date and the time
            if (props.timeOnly) {
                const valSplit = val.split(':');
                return {
                    hours: parseInt(valSplit[0]),
                    minutes: parseInt(valSplit[1]),
                };
            }
            return dayjs(val).toDate();
        }
        if (val instanceof Date) {
            return val;
        }
        return null;
    },
    set: (value) => {
        if (!value) {
            if (props.isRange) {
                emit('update:startDate', null);
                startDateValue.value = null;
                emit('update:endDate', null);
                endDateValue.value = null;
            } else {
                emit('update:modelValue', null);
                fieldValue.value = null;
            }
            return;
        }

        // Handle range values
        if (props.isRange && Array.isArray(value)) {
            const [startDate, endDate] = value;

            if (startDate) {
                const date = startDate instanceof Date ? startDate : new Date(startDate);
                let formatted;
                if (props.timeOnly) {
                    let hours = String(startDate.hours).padStart(2, '0');
                    let minutes = String(startDate.minutes).padStart(2, '0');
                    formatted = `${hours}:${minutes}`;
                } else if (props.hasTime) {
                    formatted = dayjs(date).format('YYYY-MM-DD HH:mm');
                } else {
                    formatted = dayjs(date).format('YYYY-MM-DD');
                }
                emit('update:startDate', formatted);
                startDateValue.value = formatted;
            } else {
                emit('update:startDate', null);
                startDateValue.value = null;
            }

            if (endDate) {
                const date = endDate instanceof Date ? endDate : new Date(endDate);
                let formatted;
                if (props.timeOnly) {
                    let hours = String(endDate.hours).padStart(2, '0');
                    let minutes = String(endDate.minutes).padStart(2, '0');
                    formatted = `${hours}:${minutes}`;
                } else if (props.hasTime) {
                    formatted = dayjs(date).format('YYYY-MM-DD HH:mm:ss');
                } else {
                    formatted = dayjs(date).format('YYYY-MM-DD');
                }
                emit('update:endDate', formatted);
                endDateValue.value = formatted;
            } else {
                emit('update:endDate', null);
                endDateValue.value = null;
            }
            return;
        }

        // Handle time-only values
        if (props.timeOnly) {
            // Pad hours and minutes with leading zeros if necessary
            let hours = String(value.hours).padStart(2, '0');
            let minutes = String(value.minutes).padStart(2, '0');
            let formatted = `${hours}:${minutes}`;
            emit('update:modelValue', formatted);
            fieldValue.value = formatted;
            return;
        }

        // Handle single values
        if (value instanceof Date) {
            let formatted;
            if (props.hasTime) {
                formatted = dayjs(value).format('YYYY-MM-DD HH:mm');
            } else {
                formatted = dayjs(value).format('YYYY-MM-DD');
            }
            emit('update:modelValue', formatted);
            fieldValue.value = formatted;
        } else if (typeof value === 'string') {
            emit('update:modelValue', value);
            fieldValue.value = value;
        }
    },
});

// Show placeholder when no value
const showPlaceholder = computed(() => {
    if (props.isRange) {
        const startVal = startDateValue.value || props.startDate;
        const endVal = endDateValue.value || props.endDate;
        return !startVal && !endVal;
    }
    return !fieldValue.value && !props.modelValue;
});

// Watch for external prop changes
watch(
    () => props.modelValue,
    (newVal) => {
        if (!props.isRange && fieldValue.value !== newVal) {
            fieldValue.value = newVal;
        }
    },
);

watch(
    () => props.startDate,
    (newVal) => {
        if (props.isRange && startDateValue.value !== newVal) {
            startDateValue.value = newVal;
        }
    },
);

watch(
    () => props.endDate,
    (newVal) => {
        if (props.isRange && endDateValue.value !== newVal) {
            endDateValue.value = newVal;
        }
    },
);

defineExpose({
    datePicker: datePickerRef,
});
</script>
<style scoped lang="postcss">
.dp-custom :deep(.dp__input_wrap) {
    height: 100%;
    border: 0;
}

.dp-custom :deep(.dp__input) {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    @apply -mb-px box-content border-b border-transparent !text-sm transition hover:border-brand focus:outline-none;
    @apply ml-7 h-full !w-[calc(100%-1.75rem)] border-0 bg-transparent;
    padding: 0;
}

.dp-custom :deep(.dp__input_icon) {
    left: 0.5rem;
    padding: 0;
}

.dp-custom :deep(.dp__input_wrap) {
    border: 0;
    background: transparent;
}

.dp-custom :deep(.dp__input_wrap:hover) {
    border: 0;
}

.dp-custom :deep(.dp__input:focus) {
    outline: none;
    border-bottom-color: var(--brand-color, theme('colors.brand'));
}

/* When a time suffix is appended, the value is long; reserve room on the right so the
   clear (X) button never sits flush against the text. */
.has-time-suffix .dp-custom :deep(.dp__input) {
    @apply !w-[calc(100%-1.75rem-2rem)];
}
</style>
